import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import PaymentMethods from '../../../Tienda/FastCheckout/PaymentMethods';
import usePaymentStatusPolling from '../../../Tienda/FastCheckout/usePaymentStatusPolling';

import { myOrders } from '../../../../actions/user-account';
import {
    checkUserOrderPaymentStatus,
    reconcileUserOrderPayment,
    retryUserOrderPayment
} from '../../../../actions/user-account/payment';
import { uiUserOrdersTab } from '../../../../actions/ui';
import { msg, simpleConfirmDialog } from '../../../../actions/swal_msg';
import { leaveForPayment } from '../../../../helpers/shop/couponSession';
import { returnMsg } from '../../../../helpers/shop/paymentHelper';

import {
    Box, Button, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

const PaymentMethodDialog = ({ open, setData, orderData }) => {
    const dispatch = useDispatch();
    const [activeMethod, setActiveMethod] = useState('');
    const [loading, setLoading] = useState(false);
    const resolved = useRef(false);
    const orderId = orderData?._id || '';

    const closeDialog = useCallback(() => {
        setData(value => ({ ...value, dialog1: false }));
    }, [setData]);

    const refreshOrders = useCallback(() => {
        dispatch(myOrders(() => {}));
    }, [dispatch]);

    const showPaidOrder = useCallback(reference => {
        if (resolved.current) return;
        resolved.current = true;
        closeDialog();
        dispatch(uiUserOrdersTab('1'));
        refreshOrders();
        msg(
            'success',
            'Pedido aprobado',
            reference
                ? `El pago de tu pedido ya fue confirmado. Referencia: ${reference}`
                : 'El pago de tu pedido ya fue confirmado.'
        );
    }, [closeDialog, dispatch, refreshOrders]);

    const checkStatus = useCallback(
        id => checkUserOrderPaymentStatus(id, dispatch),
        [dispatch]
    );
    const reconcile = useCallback(
        id => reconcileUserOrderPayment(id, dispatch),
        [dispatch]
    );

    usePaymentStatusPolling({
        orderId,
        enabled: open && Boolean(orderId),
        onPaid: showPaidOrder,
        checkStatus,
        reconcile,
        reconciliationContext: 'account-orders'
    });

    useEffect(() => {
        if (!open) {
            setActiveMethod('');
            setLoading(false);
            resolved.current = false;
        }
    }, [open]);

    const openContactDialog = () => {
        setData(value => ({ ...value, dialog1: false, dialog2: true }));
    };

    const proceedToPayment = async () => {
        if (!activeMethod || !orderId || loading) return;
        setLoading(true);
        const result = await dispatch(retryUserOrderPayment(activeMethod, orderId));

        if (result?.status && result.resp?.payment_url) {
            leaveForPayment(result.resp.payment_url, {
                orderId,
                context: 'account-orders'
            });
            return;
        }
        if (result?.msg === 'order-already-paid') {
            showPaidOrder(result.resp?.reference);
            return;
        }
        if (result?.msg === 'payment-method-active') {
            const provider = result.resp?.provider;
            const continuePayment = await simpleConfirmDialog(
                `Ya tienes un pago pendiente en ${provider}. Para evitar un doble cobro debes continuar con ese método.`,
                `Continuar con ${provider}`
            );
            setActiveMethod(provider || '');
            if (continuePayment && result.resp?.payment_url) {
                leaveForPayment(result.resp.payment_url, {
                    orderId,
                    context: 'account-orders'
                });
                return;
            }
        } else if ([
            'invalid-token', 'unavailable-token', 'no-token', 'account-not-found'
        ].includes(result?.msg)) {
            msg('warning', 'Sesión expirada', 'Inicia sesión nuevamente para continuar con el pago.');
        } else if (result) await returnMsg(result, activeMethod);

        setLoading(false);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            sx={{ zIndex: 1000 }}
            onClose={loading ? undefined : closeDialog}
        >
            <DialogTitle>Continuar con el pago</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Pedido ONL-{orderData?.order_num}. Antes de abrir la pasarela comprobaremos que el pedido siga pendiente y que no exista otro pago en proceso.
                </DialogContentText>
                <Button variant="text" sx={{ mb: 1 }} onClick={openContactDialog} disabled={loading}>
                    Quiero información de mi pedido
                </Button>
                <Box sx={{ mt: 1 }}>
                    <PaymentMethods
                        activeMethod={activeMethod}
                        setActiveMethod={setActiveMethod}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog} disabled={loading}>Cancelar</Button>
                <LoadingButton
                    variant="contained"
                    loading={loading}
                    disabled={!activeMethod || !orderId}
                    onClick={proceedToPayment}
                >
                    Continuar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentMethodDialog;
